import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext.tsx';
import { GoogleAuthButton } from '../components/GoogleAuthButton.tsx';
import { AppleAuthButton } from '../components/AppleAuthButton.tsx';
import { MicrosoftAuthButton } from '../components/MicrosoftAuthButton.tsx';

interface FormData {
  email: string;
  password: string;
}

interface FormErrors {
  email?: string;
  password?: string;
  general?: string;
}

export const Login: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState<FormData>({
    email: '',
    password: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(false);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      await login(formData.email.trim(), formData.password);

      // Redirect to dashboard on successful login
      navigate('/app/dashboard');
    } catch (error: any) {
      console.error('Login error:', error);
      
      if (error.response?.data?.error) {
        setErrors({ general: error.response.data.error });
      } else {
        setErrors({ general: 'Login failed. Please check your credentials and try again.' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleAuth = () => {
    console.log('=== GOOGLE AUTH DEBUG ===');
    console.log('Google auth button clicked!');
    console.log('Current URL:', window.location.href);
    console.log('Redirecting to:', '/api/auth/google');
    console.log('About to redirect...');
    // Use environment-based URL without relying on Node's process.env in the browser
    const isProd = window.location.hostname === 'plantedhire.com';
    const backendUrl = isProd
      ? 'https://plantedhire.com/api/auth/google'
      : 'http://localhost:5001/api/auth/google';
    console.log('Using URL:', backendUrl);
    window.location.href = backendUrl;
    console.log('Redirect command executed');
  };

  const handleAppleAuth = () => {
    console.log('=== APPLE AUTH DEBUG ===');
    console.log('Apple auth button clicked!');
    console.log('Current URL:', window.location.href);
    
    // Use the working configuration (no scopes, query mode)
    const appleAuthUrl = `https://appleid.apple.com/auth/authorize?` +
      `client_id=${encodeURIComponent('com.c2s.planted.si')}&` +
      `redirect_uri=${encodeURIComponent('https://plantedhire.com/api/auth/apple/callback')}&` +
      `response_type=code&` +
      `state=login&` +
      `response_mode=query`;
    
    console.log('Redirecting to Apple:', appleAuthUrl);
    window.location.href = appleAuthUrl;
  };

  const handleMicrosoftAuth = () => {
    console.log('=== MICROSOFT AUTH DEBUG ===');
    console.log('Microsoft auth button clicked!');
    console.log('Current URL:', window.location.href);
    // Use environment-based URL without relying on Node's process.env in the browser
    const isProd = window.location.hostname === 'plantedhire.com';
    const backendUrl = isProd
      ? 'https://plantedhire.com/api/auth/microsoft'
      : 'http://localhost:5001/api/auth/microsoft';
    console.log('Using URL:', backendUrl);
    window.location.href = backendUrl;
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <img 
            src="/images/site_logo.png" 
            alt="Planted" 
            className="h-12 w-auto mx-auto"
          />
          <p className="mt-2 text-sm text-gray-600">Equipment Management Platform</p>
        </div>
        <h2 className="mt-6 text-center text-2xl font-bold text-gray-900">
{t('auth.login')}
        </h2>
    
      </div>

         {/* Google OAuth Button - Outside card for testing */}
         <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-4 px-4 shadow sm:rounded-lg sm:px-10">
            <div className="text-center mb-4">
             
            </div>
            <div className="space-y-3">
              <GoogleAuthButton 
                onClick={handleGoogleAuth}
                disabled={isLoading}
              />
              <MicrosoftAuthButton 
                onClick={handleMicrosoftAuth}
                disabled={isLoading}
              />
              <AppleAuthButton 
                onClick={handleAppleAuth}
                disabled={isLoading}
              />
            </div>
          </div>
        </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="card">
          <div className="card-body">
            <form className="space-y-6" onSubmit={handleSubmit}>
              {errors.general && (
                <div className="bg-danger-50 border border-danger-200 rounded-lg p-4">
                  <p className="text-sm text-danger-600">{errors.general}</p>
                </div>
              )}

              <div>
              <div className="text-sm text-gray-500 mb-9">Or go old school with ...</div>
                <label htmlFor="email" className="form-label">
                  Email Address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className={`form-input ${errors.email ? 'border-danger-300 focus:border-danger-500 focus:ring-danger-500' : ''}`}
                  placeholder="your.email@company.com"
                  value={formData.email}
                  onChange={handleInputChange}
                />
                {errors.email && (
                  <p className="form-error">{errors.email}</p>
                )}
              </div>

              <div>
                <label htmlFor="password" className="form-label">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  className={`form-input ${errors.password ? 'border-danger-300 focus:border-danger-500 focus:ring-danger-500' : ''}`}
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={handleInputChange}
                />
                {errors.password && (
                  <p className="form-error">{errors.password}</p>
                )}
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="remember-me"
                    name="remember-me"
                    type="checkbox"
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900">
                    Remember me
                  </label>
                </div>

                <div className="text-sm">
                  <button type="button" className="font-medium text-primary-600 hover:text-primary-500 bg-transparent border-none p-0 cursor-pointer">
                    Forgot your password?
                  </button>
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full btn btn-primary"
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Signing in...
                    </div>
                  ) : (
                    t('auth.login')
                  )}
                </button>
              </div>
            </form>

            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">Don't have an account?</span>
                </div>
              </div>

              <div className="mt-6">
                <Link
                  to="/signup"
                  className="w-full btn btn-outline-primary"
                >
                  Start your free trial
                </Link>
              </div>
            </div>
          </div>
        </div>

     

        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500">
            Secure login powered by industry-standard encryption
          </p>
        </div>
      </div>
    </div>
  );
};
